import React from 'react';
import Image from 'next/image';
import DevicePartnerBadge from '@/components/ui/DevicePartnerBadge/DevicePartnerBadge';
import type { LanguageType } from '@/lib/types';

interface DeviceShowcaseCardProps {
  imageUrl: string;
  imageAlt: string;
  /** Device name — rendered as the card heading and passed to `<DevicePartnerBadge title>`. */
  title: string;
  description: string;
  /** Drives the LTR/RTL responsive-padding swap on the text block. */
  language: LanguageType;
  /** Badge sizing — the caller resolves `isLg` and passes the chosen variant. */
  badgeSize: 'desktop' | 'mobile';
}

/**
 * Single card for DevicesSection's responsive grid (no swiper variant —
 * this section is grid-only across all breakpoints).
 *
 * The `device-image-card` className on the image is intentional: the section
 * queries it with `document.querySelectorAll('.device-image-card')` to wire
 * the GSAP parallax animation. Do not rename without updating that selector.
 *
 * The image wrapper uses `outline-*` (paints outside the box, doesn't get
 * clipped by `overflow-hidden`) — matches the original markup.
 */
const DeviceShowcaseCard: React.FC<DeviceShowcaseCardProps> = ({
  imageUrl,
  imageAlt,
  title,
  description,
  language,
  badgeSize,
}) => {
  return (
    <div className="relative w-full">
      <div className="flex flex-col gap-[1.5rem]">
        <div className="
          border-radius-main
          outline-secondary outline-stroke-soft-200
          overflow-hidden
          ">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={600}
            height={800}
            sizes="(min-width: 992px) 25vw, 100vw"
            loading="eager"
            style={{
              scale: 1.1,
              width: '100%',
              height: 'auto',
              aspectRatio: '3 / 4',
              objectFit: 'cover',
            }}
            className="device-image-card"
          />
        </div>
        <div
          className={`
            flex flex-col gap-[1rem] lg:gap-[1.25rem]
            pl-[0.5rem] pr-[0.5rem]
            ${language === 'english' ?
              'lg:pl-[0.25rem] lg:pr-[1.5rem]'
              :
              'lg:pr-[0.25rem] lg:pl-[1.5rem]'
            }
          `}>
          <div className="flex flex-row gap-[1rem] lg:flex-col justify-between lg:items-start lg:gap-[1.25rem]">
            <div className="w-fit order-2 lg:order-1">
              <DevicePartnerBadge title={title} size={badgeSize} />
            </div>
            <h3 className="
              order-1 lg:order-2
              text-text-primary
              text-28-to-20--neue-june
            ">
              {title}
            </h3>
          </div>

          <p className="
            text-text-quaternary
            text-18--alexandria
          ">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DeviceShowcaseCard;
